import { Component, Inject, signal } from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatButton } from '@angular/material/button';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatOption, MatSelect } from '@angular/material/select';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../../app.component';
import { Role } from '../../../enums';
import {
  ScheduleOverviewPeriod,
  ScheduleOverviewRole,
  SendScheduleOverviewRequest,
  SendScheduleOverviewResponse,
} from '../../../interfaces';

interface SendScheduleDialogData {
  orgId: string;
  coDriverEnabled: boolean;
}

@Component({
  selector: 'app-send-schedule-dialog',
  templateUrl: './send-schedule-dialog.component.html',
  styleUrls: ['./send-schedule-dialog.component.scss'],
  imports: [
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatDialogClose,
    MatButton,
    MatFormField,
    MatLabel,
    MatSelect,
    MatOption,
  ],
})
export class SendScheduleDialogComponent {
  period = signal<ScheduleOverviewPeriod | null>(null);
  selectedRoles = signal<ScheduleOverviewRole[]>([]);
  loading = signal(false);

  readonly roles: { value: ScheduleOverviewRole; label: string }[];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: SendScheduleDialogData,
    private dialogRef: MatDialogRef<SendScheduleDialogComponent>,
    private snackBar: MatSnackBar,
  ) {
    this.roles = [
      { value: Role.Driver, label: 'Chauffeur' },
      ...(data.coDriverEnabled
        ? [{ value: Role.CoDriver as ScheduleOverviewRole, label: 'Bijrijder' }]
        : []),
      { value: Role.Planner, label: 'Planner' },
      { value: Role.RidePlanner, label: 'Ritplanner' },
    ];
  }

  canSend(): boolean {
    return !!this.period() && this.selectedRoles().length > 0;
  }

  async sendSchedule(): Promise<void> {
    if (!this.canSend() || this.loading()) {
      return;
    }

    this.loading.set(true);
    try {
      const sendOverview = httpsCallable<SendScheduleOverviewRequest, SendScheduleOverviewResponse>(
        functions,
        'httpSendScheduleOverview',
      );
      const response = await sendOverview({
        orgId: this.data.orgId,
        period: this.period()!,
        roles: this.selectedRoles(),
      });
      const { sent, failed, skipped } = response.data;

      if (failed > 0) {
        this.snackBar.open(
          `Rooster deels verstuurd: ${sent} gelukt, ${failed} mislukt.`,
          'Sluiten',
          { duration: 7000 },
        );
      } else if (sent === 0 && skipped === 0) {
        this.snackBar.open('Geen medewerkers met diensten gevonden.', 'Sluiten', {
          duration: 5000,
        });
      } else {
        const skippedMessage = skipped
          ? ` ${skipped} rooster-e-mail(s) overgeslagen wegens een ontbrekend e-mailadres.`
          : '';
        this.snackBar.open(`${sent} rooster-e-mail(s) verstuurd.${skippedMessage}`, 'Sluiten', {
          duration: 6000,
        });
      }
      this.dialogRef.close(response.data);
    } catch {
      this.snackBar.open('Rooster versturen is mislukt. Probeer het opnieuw.', 'Sluiten', {
        duration: 7000,
      });
    } finally {
      this.loading.set(false);
    }
  }
}
